/**
 * Dropdown with search functionality
 * Known issues:
 * - When dropdown is opened Voiceover + safari combination does not announce the first/active option
 * - `tom-select` does not work with older browsers (IE 11) and relies on features that can't be polyfilled (E.g. RegExp unicode flag)
 */

import TomSelect from 'tom-select';
import axios from 'axios';
import 'tom-select/dist/css/tom-select.css';

const selectSearch = (() => {
  const LANG = document.documentElement.lang;
  const NO_RESULTS = {
    fi: 'Ei hakutuloksia',
    en: 'No results',
    sv: 'Inga resultat'
  };

  /**
   * Initilizes given elements
   * @param {Array} selects native select elements optional
   * @returns {Array} Instances of Tom Select
   */
  function initMultiple(selects) {
    const searchSelects = selects.map((select) => {
      const config = getDefaultConfig();
      const ajaxPath = select.dataset.ajax;
      if(ajaxPath) {
        //load results from backend
        config.loadThrottle = 500;
        config.load = function(query, callback) {
          loadResults(query, callback, ajaxPath);
        };
      }
      const tomSelect = new TomSelect(select, config);
      improveScreenReaderSupport(tomSelect);
      return tomSelect;
    });

    return searchSelects;
  }

  /**
   * Initilizes given element
   * @param {HTMLElement} select native select element
   * @param {Object} config Tom select configuration
   * @returns {HTMLElement} Tom Select instance
  */
  function initSingle(select, config = {}) {
    const configuration = Object.assign(getDefaultConfig(select.hasAttribute('multiple')), config);
    const tomSelect = new TomSelect(select, configuration);
    improveScreenReaderSupport(tomSelect);
    return tomSelect;
  }

  /**
   * Get config with locatisation setting
   * @param {boolean} [isMultiselect] Is this a multiselect, defaults to false
   * @returns {Object} Tom Select config
   */
  function getDefaultConfig(isMultiselect = false) {
    return {
      // as a select replacement we don't want to force people to type (default only 50)
      maxOptions: 3000,
      allowEmptyOption: isMultiselect ? false : true,
      render: {
        no_results: () => {
          return `<div class="no-results">${NO_RESULTS[LANG] || NO_RESULTS.en}</div>`;
        }
      }
    };
  }

  /**
   * Search results
   * @param {string} query search string
   * @param {function} callback TomSelect load callback
   * @param {string} ajaxPath ajax path
   */
  async function loadResults(query, callback, ajaxPath) {
    if(ajaxPath) {
      var url = '/stage-ajax/'+ ajaxPath;
      await axios.get(url, {
        params: {
          q: query,
          'stage-language': LANG,
        }
      }).then((response) => {
        callback(response.data.options);
      }).catch((error) => {
        callback();
      });
    }
  }

  /**
   * Additional functionality to make tom select work in voice-over + safari combination
   * @param {object} tomSelect Tom select instance
   */
  function improveScreenReaderSupport(tomSelect) {
    tomSelect.control_input.setAttribute('aria-owns',tomSelect.dropdown_content.id);

    /* Voiceover on safari loses focus when moving from input to next item (Control + Option + right arrow).
    This is a hacky way to force focus to item right next to the input.
    */
    tomSelect.wrapper.after(document.createElement('span'));
    const focusHelper = tomSelect.wrapper.nextSibling;
    focusHelper.setAttribute('tabindex', '-1');

    tomSelect.control_input.addEventListener('focusout', (event) => {
      //if target element would be dropdown, but it is not visible
      if(event.relatedTarget && event.relatedTarget === tomSelect.dropdown_content && window.getComputedStyle(event.relatedTarget.parentElement).display==='none') {
        focusHelper.setAttribute('aria-label', tomSelect.wrapper.innerText);
        focusHelper.focus();
      }
    });
  }

  return {
    initSingle,
    initMultiple
  };

})();

export default selectSearch;

